import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import _ from "lodash";

import {
  Footer,
  SidebarContainer,
  MobileAlert,
  MobileNav,
  NavigationMenuDesktop,
  SiteBanner,
} from "../../components";
import {
  GuidedSearchSidebar,
  FilteredSearchSidebar,
  ToolDetailSidebar,
} from "../";
import { routeNames } from "../../routes";
import { browserUtils } from "../../utils";
import { fetchPage } from "../../store/modules/content.module";
import { signOut, getCurrentUser } from "../../store/modules/auth.module";
import { setScrollPosY } from "../../store/modules/ui.module";
import styles from "./App.sass";

class App extends Component {
  constructor() {
    super();
    this.routesThatMaintainScrollPos = [routeNames.filteredSearch];
    this.isMobile = browserUtils.isMobileDevice();
  }

  loadContentForRoute(pathname) {
    const { content, fetchPage } = this.props;

    const contentSlugByPath = {
      [routeNames.root]: "home",
      [routeNames.resources]: "resources",
      [routeNames.guidedSearch]: "glossary",
      [routeNames.filteredSearch]: "glossary",
      [`${routeNames.resources}${routeNames.glossary}`]: "glossary",
      [`${routeNames.resources}${routeNames.caseStudies}`]: "case-studies",
      [`${routeNames.resources}${routeNames.resourceLibraries}`]:
        "resource-libraries",
      [`${routeNames.resources}${routeNames.dataSources}`]: "data-sources",
      [`${routeNames.resources}${routeNames.faq}`]: "faqs",
      [`${routeNames.resources}${routeNames.retiredTools}`]: "retired-tools",
      [routeNames.about]: "about",
      [routeNames.partners]: "partners",
      [routeNames.funding]: "funding",
      [routeNames.contact]: "contact",
      [routeNames.privacyPolicy]: "privacy-policy",
    };

    const slug = contentSlugByPath[pathname];

    // Non-CMS routes (search, tool detail, 404, etc.) do not need page content.
    if (!slug) {
      return;
    }

    if (!content.data[slug]) {
      fetchPage(slug);
    }
  }

  componentDidMount() {
    this.loadContentForRoute(this.props.location.pathname);
    this.props.getCurrentUser();
  }

  componentWillReceiveProps(nextProps) {
    // Before changing routes, update global scroll pos if leaving filtered search
    if (this.props.location !== nextProps.location) {
      if (
        this.routesThatMaintainScrollPos.includes(this.props.location.pathname)
      ) {
        this.props.setScrollPosY(this.appContent.scrollTop);
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      this.loadContentForRoute(this.props.location.pathname);

      // Scroll App content to top when navigating to new route/component unless going back to filtered search results
      if (
        this.routesThatMaintainScrollPos.includes(this.props.location.pathname)
      ) {
        this.appContent.scrollTop = this.props.scrollPosY;
      } else {
        this.appContent.scrollTop = 0;
      }
    }
  }

  renderSidebarContents() {
    // Special case here to account for tool pathname that has variable url param (/:id) -- just do partial string check
    if (_.includes(this.props.location.pathname, routeNames.tool)) {
      return <SidebarContainer>{() => <ToolDetailSidebar />}</SidebarContainer>;
    }

    switch (this.props.location.pathname) {
      case routeNames.guidedSearch:
        return (
          <SidebarContainer>
            {(isExpanded, toggleSidebar) => (
              <GuidedSearchSidebar
                isExpanded={isExpanded}
                toggleSidebar={toggleSidebar}
              />
            )}
          </SidebarContainer>
        );

      case routeNames.filteredSearch:
        return (
          <SidebarContainer className="sidebar-container--filtered-search">
            {() => <FilteredSearchSidebar />}
          </SidebarContainer>
        );

      default:
        return (
          <SidebarContainer>{() => <NavigationMenuDesktop />}</SidebarContainer>
        );
    }
  }

  render() {
    return (
      <div className="app-container-wrapper">
        <Helmet>
          <title>Gulf TREE</title>
        </Helmet>
        <SiteBanner />
        <div className="app-container">
          {this.renderSidebarContents()}
          <div className="app-content" ref={(el) => (this.appContent = el)}>
            {this.isMobile && (
              <MobileAlert pathname={this.props.location.pathname} />
            )}
            <MobileNav />
            <main role="main" className="app-content-main">
              {this.props.children}
            </main>
            <Footer
              signOut={this.props.signOut}
              isLoggedIn={_.has(this.props.currentUser, "id")}
            />
          </div>
        </div>
      </div>
    );
  }
}

App.propTypes = {
  children: PropTypes.element,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  scrollPosY: PropTypes.number,
  setScrollPosY: PropTypes.func,
  fetchPage: PropTypes.func,
  signOut: PropTypes.func,
  getCurrentUser: PropTypes.func,
};

const mapStateToProps = (state) => ({
  content: state.content,
  currentUser: state.auth.currentUser,
  scrollPosY: state.ui.scrollPosY,
});

export default connect(mapStateToProps, {
  fetchPage,
  signOut,
  getCurrentUser,
  setScrollPosY,
})(App);
